import React, { useState, useEffect } from 'react';
import { Mail, Bell, BellOff, Clock, Send, Users, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DailySummarySubscription = ({ token, isAdmin = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [showSubscribers, setShowSubscribers] = useState(false);

  // Charger le statut d'abonnement
  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/digest/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Erreur chargement statut:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger la liste des abonnés (admin)
  const fetchSubscribers = async () => {
    if (!isAdmin) return;
    try {
      const response = await fetch(`${API_URL}/api/email/digest/subscribers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers || []);
      }
    } catch (error) {
      console.error('Erreur chargement abonnés:', error);
    }
  };

  // S'abonner
  const handleSubscribe = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/email/digest/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Abonné au résumé quotidien');
        fetchStatus();
        fetchSubscribers();
      } else {
        toast.error(data.detail || 'Erreur lors de l\'abonnement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  // Se désabonner
  const handleUnsubscribe = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/email/digest/unsubscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Désabonné du résumé quotidien');
        fetchStatus();
        fetchSubscribers();
      } else {
        toast.error(data.detail || 'Erreur lors du désabonnement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  // Envoyer immédiatement (admin)
  const handleSendNow = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/email/digest/send-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Résumé envoyé à ${data.sent} abonné(s)`);
      } else {
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchSubscribers();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-black border-2 border-marine-cyan/50 p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-marine-cyan animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black border-2 border-marine-cyan/50" data-testid="daily-summary-panel">
      {/* Header */}
      <div className="bg-marine-cyan/20 px-4 py-3 border-b border-marine-cyan/50 flex items-center justify-between">
        <span className="font-mono text-marine-cyan text-sm uppercase tracking-wider flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Résumé Quotidien par Email
        </span>
        <button
          onClick={() => { fetchStatus(); fetchSubscribers(); }}
          className="text-marine-cyan hover:text-white"
          title="Actualiser"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Info Box */}
        <div className="bg-marine-slate/20 p-4 border-l-2 border-marine-cyan">
          <p className="font-mono text-white/80 text-sm">
            Recevez chaque matin un résumé des alertes stratégiques des dernières 24h directement dans votre boîte mail.
          </p>
          {status && (
            <div className="mt-3 flex items-center gap-4 text-xs font-mono text-white/80">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Envoi prévu : {status.digest_hour || "07:00 UTC"}
              </span>
            </div>
          )}
        </div>

        {/* Subscription Status */}
        <div className="flex items-center justify-between p-4 bg-marine-slate/10 border border-marine-slate/30">
          <div className="flex items-center gap-3">
            {status?.user_subscribed ? (
              <>
                <Bell className="w-6 h-6 text-marine-green" />
                <div>
                  <p className="font-mono text-marine-green text-sm font-bold">Abonné</p>
                  <p className="font-mono text-white/80 text-xs">{status.user_email}</p>
                </div>
              </>
            ) : (
              <>
                <BellOff className="w-6 h-6 text-white/80" />
                <div>
                  <p className="font-mono text-white/90 text-sm">Non abonné</p>
                  <p className="font-mono text-white/80 text-xs">{status?.user_email}</p>
                </div>
              </>
            )}
          </div>

          <button
            onClick={status?.user_subscribed ? handleUnsubscribe : handleSubscribe}
            disabled={actionLoading}
            className={`px-4 py-2 font-mono text-xs uppercase flex items-center gap-2 transition-colors ${
              status?.user_subscribed
                ? 'bg-red-900/30 border border-red-500/50 text-red-400 hover:bg-red-900/50'
                : 'bg-marine-green/20 border border-marine-green text-marine-green hover:bg-marine-green hover:text-black'
            }`}
            data-testid={status?.user_subscribed ? "unsubscribe-btn" : "subscribe-btn"}
          >
            {actionLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : status?.user_subscribed ? (
              <>
                <BellOff className="w-4 h-4" />
                Se désabonner
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                S'abonner
              </>
            )}
          </button>
        </div>

        {/* Admin Section */}
        {isAdmin && (
          <div className="border-t border-marine-slate/30 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-marine-amber text-sm uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4" />
                Administration
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSubscribers(!showSubscribers)}
                  className="px-3 py-1.5 bg-marine-slate/20 border border-marine-slate/50 text-white/90 hover:text-white font-mono text-xs"
                >
                  {showSubscribers ? 'Masquer' : 'Voir'} abonnés ({subscribers.length})
                </button>
                <button
                  onClick={handleSendNow}
                  disabled={actionLoading || subscribers.length === 0}
                  className="px-3 py-1.5 bg-marine-amber/20 border border-marine-amber text-marine-amber hover:bg-marine-amber hover:text-black font-mono text-xs flex items-center gap-2 disabled:opacity-50"
                  data-testid="send-now-btn"
                >
                  <Send className="w-3 h-3" />
                  Envoyer maintenant
                </button>
              </div>
            </div>

            {/* Subscribers List */}
            {showSubscribers && (
              <div className="bg-marine-slate/10 border border-marine-slate/30 max-h-48 overflow-y-auto">
                {subscribers.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-marine-slate/30">
                      <tr className="font-mono text-white/80 text-xs uppercase">
                        <th className="text-left p-2">Email</th>
                        <th className="text-left p-2">Nom</th>
                        <th className="text-left p-2">Depuis</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-marine-slate/20">
                      {subscribers.map((sub, idx) => (
                        <tr key={idx} className="hover:bg-marine-slate/10">
                          <td className="p-2 font-mono text-white/80 text-xs">{sub.email}</td>
                          <td className="p-2 font-mono text-white/80 text-xs">{sub.user_name || '-'}</td>
                          <td className="p-2 font-mono text-white/80 text-xs">
                            {sub.created_at ? new Date(sub.created_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="p-4 text-center font-mono text-white/80 text-sm">Aucun abonné</p>
                )}
              </div>
            )}

            {/* Scheduler Status */}
            {status && (
              <div className="flex items-center gap-4 text-xs font-mono text-white/80">
                <span>Scheduler: {status.running ? 
                  <span className="text-marine-green">Actif</span> : 
                  <span className="text-red-400">Inactif</span>
                }</span>
                {status.next_digest_time && (
                  <span>Prochain envoi: {new Date(status.next_digest_time).toLocaleString('fr-FR')}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailySummarySubscription;
